import { Show, createEffect } from "solid-js";
import { Portal } from "solid-js/web";

import closeSvg from "@uic/assets/icons/x-circle-bold.svg?raw";

import { KEYBINDINGS, matchesKeybinding } from "../keybindings";

import { UIButton } from "./button";
import { UIIcon } from "./icon";

/**
 * @typedef {object} UIModalProps
 * @property {boolean} isOpen - Whether modal is open
 * @property {() => void} onClose - Close callback
 * @property {string | import('solid-js').JSX.Element} [title] - Modal title
 * @property {import('solid-js').JSX.Element} children - Modal content
 * @property {import('solid-js').JSX.Element} [actions] - Action buttons
 * @property {boolean} [showClose=true] - Show the corner close button
 * @property {'md' | 'lg' | 'xl' | '2xl'} [size='md'] - Widest the box grows on a desktop screen
 * @property {'default' | 'tall'} [height='default'] - Shortest the box gets on a desktop screen
 * @property {'default' | 'error'} [titleTone='default'] - Semantic colour of the title
 * @property {string} [className] - Additional CSS classes
 */

const SIZE_CLASSES = {
  md: "",
  lg: "tw:max-w-lg",
  xl: "tw:max-w-xl",
  "2xl": "tw:max-w-2xl",
};

/**
 * A modal holding a control that opens a menu takes `tall`, reserving height it
 * may not need so the menu has somewhere to go: the box scrolls its own
 * overflow, which would otherwise cut the menu off at the bottom edge. Below the
 * `md` breakpoint the box is already full-screen, so the floor only applies
 * above it.
 */
const HEIGHT_CLASSES = {
  default: "",
  tall: "tw:md:min-h-80",
};

const TITLE_TONE_CLASSES = {
  default: "",
  error: "tw:text-error",
};

/**
 * A modal component using DaisyUI
 * @param {UIModalProps} props Component props
 * @returns {import('solid-js').JSX.Element} Modal
 */
export function UIModal(props) {
  let dialogRef;

  createEffect(() => {
    if (props.isOpen && dialogRef) {
      dialogRef.showModal();
    } else if (!props.isOpen && dialogRef) {
      dialogRef.close();
    }
  });

  const handleClose = (e) => {
    if (e.target === dialogRef) {
      props.onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (matchesKeybinding(e, KEYBINDINGS.CANCEL)) {
      e.stopPropagation();
      props.onClose();
    }
  };

  return (
    <Portal>
      <dialog
        ref={dialogRef}
        className={`tw:d-modal ${props.className || ""}`}
        onClick={handleClose}
        onKeyDown={handleKeyDown}
        onClose={() => props.onClose()}
      >
        <div
          className={`tw:relative tw:d-modal-box tw:rounded-none tw:slorg-shadow-md ${SIZE_CLASSES[props.size || "md"]} ${HEIGHT_CLASSES[props.height || "default"]} tw:max-md:size-full tw:max-md:max-h-full tw:max-md:max-w-full`}
          onClick={(e) => e.stopPropagation()}
        >
          <Show when={props.showClose !== false}>
            <UIButton
              variant="error"
              size="sm"
              className="tw:absolute tw:top-2 tw:right-2 tw:rounded-none"
              aria-label="Close"
              onClick={() => props.onClose()}
            >
              <UIIcon svg={closeSvg} />
            </UIButton>
          </Show>

          <Show when={props.title}>
            <h3
              className={`tw:mb-4 tw:pr-12 tw:font-small-caps tw:text-section ${TITLE_TONE_CLASSES[props.titleTone || "default"]}`}
            >
              {props.title}
            </h3>
          </Show>

          <div className="tw:py-4">{props.children}</div>

          <Show when={props.actions}>
            <div className="tw:d-modal-action">{props.actions}</div>
          </Show>
        </div>
      </dialog>
    </Portal>
  );
}
